"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { spinTarget, winningIndex } from "@/lib/tool-kit/random-tools/wheel";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 20;
const SPIN_DURATION_MS = 4000;

const WHEEL_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#d97706",
  "#7c3aed",
  "#db2777",
  "#0891b2",
  "#65a30d",
  "#ea580c",
  "#4f46e5",
  "#0d9488",
  "#b91c1c",
  "#9333ea",
  "#ca8a04",
  "#0284c7",
  "#15803d",
  "#c026d3",
  "#e11d48",
  "#059669",
  "#6d28d9",
];

const WHEEL_SIZE = 300;
const WHEEL_CENTER = WHEEL_SIZE / 2;
const WHEEL_RADIUS = WHEEL_CENTER - 5;
const LABEL_RADIUS = WHEEL_RADIUS * 0.62;
const MAX_LABEL_CHARS = 12;

/** Point on a circle at `angleDeg`, measured clockwise from 12 o'clock. */
function polarPoint(radius: number, angleDeg: number): [number, number] {
  const radians = (angleDeg * Math.PI) / 180;
  return [
    WHEEL_CENTER + radius * Math.sin(radians),
    WHEEL_CENTER - radius * Math.cos(radians),
  ];
}

/** SVG path for a pie segment between two clockwise angles from 12 o'clock. */
function segmentPath(startDeg: number, endDeg: number): string {
  const [startX, startY] = polarPoint(WHEEL_RADIUS, startDeg);
  const [endX, endY] = polarPoint(WHEEL_RADIUS, endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${WHEEL_CENTER} ${WHEEL_CENTER}`,
    `L ${startX} ${startY}`,
    `A ${WHEEL_RADIUS} ${WHEEL_RADIUS} 0 ${largeArc} 1 ${endX} ${endY}`,
    "Z",
  ].join(" ");
}

function truncateLabel(label: string): string {
  if (label.length <= MAX_LABEL_CHARS) {
    return label;
  }
  return `${label.slice(0, MAX_LABEL_CHARS - 1)}…`;
}

function parseOptions(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * The options box. It is locked while the wheel turns: an edit mid-spin would rebuild
 * the segments under a result that was already fixed for the old list.
 */
export function WheelOptionsInput({
  value,
  placeholder,
  disabled,
  onChange,
}: {
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <textarea
      className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
      disabled={disabled}
      id="wheel-input"
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      value={value}
    />
  );
}

function WheelSvg({
  options,
  rotation,
  ariaLabel,
}: {
  options: string[];
  rotation: number;
  ariaLabel: string;
}) {
  const segmentDeg = 360 / options.length;
  return (
    <svg
      aria-label={ariaLabel}
      className="h-auto w-full max-w-xs"
      role="img"
      viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
    >
      <g
        style={{
          transform: `rotate(${rotation}deg)`,
          transformOrigin: "50% 50%",
          transition: `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
        }}
      >
        {options.map((option, index) => {
          const start = index * segmentDeg;
          const midAngle = start + segmentDeg / 2;
          const [labelX, labelY] = polarPoint(LABEL_RADIUS, midAngle);
          return (
            <g key={`${option}-${String(index)}`}>
              <path
                d={segmentPath(start, start + segmentDeg)}
                fill={WHEEL_COLORS[index % WHEEL_COLORS.length]}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
              <text
                fill="#ffffff"
                fontSize={12}
                fontWeight={600}
                textAnchor="middle"
                transform={`rotate(${midAngle} ${labelX} ${labelY})`}
                x={labelX}
                y={labelY}
              >
                {truncateLabel(option)}
              </text>
            </g>
          );
        })}
      </g>
      {/* Pointer at 12 o'clock */}
      <polygon
        fill="currentColor"
        points={`${WHEEL_CENTER - 10},2 ${WHEEL_CENTER + 10},2 ${WHEEL_CENTER},26`}
      />
    </svg>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const WHEEL_SPINNER_STRINGS = {
  optionsLabel: "Options — one per line ({min}–{max})",
  placeholder: "Pizza\nSushi\nBurgers\nTacos",
  optionsOnWheel:
    "one:{count} option on the wheel|other:{count} options on the wheel",
  optionsOnWheelTooFew:
    "one:{count} option on the wheel — add at least 2 to spin|other:{count} options on the wheel — add at least 2 to spin",
  optionsOnWheelTooMany:
    "one:{count} option on the wheel — remove some, the wheel holds at most {max}|other:{count} options on the wheel — remove some, the wheel holds at most {max}",
  wheelAriaLabel: "Spinner wheel",
  spinning: "Spinning…",
  spin: "Spin",
  resetWheel: "Reset Wheel",
  removeWinnerAfterSpin: "Remove winner after spin",
  theWheelPicked: "The wheel picked",
  randomnessNote:
    "Every spin lands on a uniformly random angle, so each option on the wheel has an equal chance — the animation is for suspense, not the outcome.",
  defaultOptions: "Pizza\nSushi\nBurgers\nTacos\nSalad\nCurry",
} as const;

function optionsStatus(
  tooFew: boolean,
  tooMany: boolean,
  strings: typeof WHEEL_SPINNER_STRINGS
): string {
  if (tooFew) {
    return strings.optionsOnWheelTooFew;
  }
  return tooMany ? strings.optionsOnWheelTooMany : strings.optionsOnWheel;
}

export default function WheelSpinnerWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(WHEEL_SPINNER_STRINGS);
  const [input, setInput] = useState<string>(strings.defaultOptions);
  const [pool, setPool] = useState<string[] | null>(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);
  const [removeWinner, setRemoveWinner] = useState(false);

  const parsed = parseOptions(input);
  const options = pool ?? parsed;
  const tooFew = options.length < MIN_OPTIONS;
  const tooMany = options.length > MAX_OPTIONS;
  const canSpin = !(spinning || tooFew || tooMany);

  function handleInputChange(value: string) {
    setInput(value);
    setPool(null);
    setWinner(null);
  }

  function handleSpin() {
    if (!canSpin) {
      return;
    }
    const spinOptions = options.slice();
    const target = spinTarget(rotation, spinOptions.length);
    setSpinning(true);
    setWinner(null);
    setRotation(target);
    setTimeout(() => {
      const index = winningIndex(target, spinOptions.length);
      setWinner(spinOptions[index]);
      if (removeWinner) {
        setPool(spinOptions.filter((_, i) => i !== index));
      }
      setSpinning(false);
    }, SPIN_DURATION_MS);
  }

  function handleReset() {
    setPool(null);
    setWinner(null);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="wheel-input"
        >
          {strings.optionsLabel
            .replace("{min}", String(MIN_OPTIONS))
            .replace("{max}", String(MAX_OPTIONS))}
        </label>
        <WheelOptionsInput
          disabled={spinning}
          onChange={handleInputChange}
          placeholder={strings.placeholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(
            optionsStatus(tooFew, tooMany, strings),
            locale,
            options.length,
            {
              max: MAX_OPTIONS,
            }
          )}
        </p>
      </div>

      {/* Wheel */}
      {!(tooFew || tooMany) && (
        <div className="flex flex-col items-center gap-6 rounded-lg border border-border bg-card p-6 text-foreground">
          <WheelSvg
            ariaLabel={strings.wheelAriaLabel}
            options={options}
            rotation={rotation}
          />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              className="rounded-lg bg-primary px-8 py-3 font-medium text-base text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
              disabled={!canSpin}
              onClick={handleSpin}
              type="button"
            >
              {spinning ? strings.spinning : strings.spin}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
              disabled={spinning || (pool === null && winner === null)}
              onClick={handleReset}
              type="button"
            >
              {strings.resetWheel}
            </button>
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={removeWinner}
              className="accent-primary"
              onChange={(e) => setRemoveWinner(e.target.checked)}
              type="checkbox"
            />
            {strings.removeWinnerAfterSpin}
          </label>
        </div>
      )}

      {/* Winner */}
      {winner !== null && (
        <div className="rounded-lg border border-primary bg-primary/10 p-6 text-center">
          <p className="text-muted-foreground text-sm">
            {strings.theWheelPicked}
          </p>
          <p className="mt-2 font-semibold text-3xl text-foreground">
            {winner}
          </p>
        </div>
      )}

      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.randomnessNote}
      </p>
    </>
  );
}
